import { useState } from "react";

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Paper, Popover, useTheme, IconButton, Typography } from "@mui/material";

interface QuestionMarkInfoPopupProps {
    /**
     * The message to display in the popup. Can include HTML tags like strong, em, br/, etc.
     * Example: "<strong>Important</strong> information here"
     * @example
     * message="<strong>Secondary insurance</strong> helps cover additional costs"
     */
    message: string;
}

/**
 * A popup component that displays a help icon which, when clicked, shows a message in a popover.
 * The message can contain HTML tags for formatting.
 *
 * @example
 * <QuestionMarkInfoPopup message="<strong>Important</strong> information here" />
 */
export default function QuestionMarkInfoPopup({ message }: QuestionMarkInfoPopupProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const theme = useTheme();
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton
                onClick={handleClick}
                size="small"
                sx={{
                    color: 'text.secondary',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.1)',
                    },
                    '&:active': {
                        transform: 'scale(0.95)',
                    }
                }}
                aria-label="Help information"
            >
                <HelpOutlineIcon fontSize="small" />
            </IconButton>
            <Popover
                id='help-popover'
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: '1px solid',
                        borderColor: 'divider',
                        mt: 1,
                        overflow: 'visible',
                    }
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '12px solid transparent',
                            borderRight: '12px solid transparent',
                            borderBottom: `12px solid ${theme.palette.divider}`,
                            zIndex: 1,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: -10,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '10px solid transparent',
                            borderRight: '10px solid transparent',
                            borderBottom: `10px solid ${theme.palette.background.paper}`,
                            zIndex: 2,
                        }
                    }}
                >
                    <Box sx={{
                        p: 2,
                        maxWidth: 320,
                        minWidth: 200,
                    }}>
                        <Typography
                            variant="body2"
                            sx={{
                                lineHeight: 1.5,
                                color: 'text.primary',
                                '& strong': {
                                    fontWeight: 600,
                                    color: 'text.primary',
                                },
                                '& em': {
                                    fontStyle: 'italic',
                                    color: 'text.secondary',
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: message }}
                        />
                    </Box>
                </Paper>
            </Popover>
        </>
    )
}
